import { Component } from "react";
import { AlertCircle, ArrowLeft, Home, ListRestart, RefreshCw } from "lucide-react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    // You can also log the error to an error reporting service
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex flex-col">
          {/* Main Content */}
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="max-w-2xl w-full bg-white rounded-xl shadow-xl p-10 mx-4">
              <div className="max-w-xl mx-auto text-center">
                {/* Error Icon */}
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8">
                  <AlertCircle className="w-10 h-10 text-red-600" />
                </div>

                {/* Error Message */}
                <h1 className="text-3xl font-bold text-gray-800 mb-3">
                  Oops! Something went wrong
                </h1>
                <p className="text-lg text-gray-600 mb-10">
                  We're sorry, but there was an error processing your request.
                  Our team has been notified and is working to fix this issue.
                </p>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                  <button
                    onClick={() => window.location.reload()}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-lg"
                  >
                    <RefreshCw className="w-5 h-5" />
                    Reload Page
                  </button>
                  <button
                    onClick={() => window.location.href = "/"}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-lg"
                  >
                    <Home className="w-5 h-5" />
                    Go to Dashboard
                  </button>
                </div>

                {/* Error Details (Development Only) */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="mt-8 p-6 bg-gray-50 rounded-lg text-left">
                    <details className="group">
                      <summary className="text-base text-gray-600 cursor-pointer hover:text-gray-800 transition-colors">
                        Error Details
                      </summary>
                      <pre className="mt-4 text-sm text-gray-500 overflow-auto p-4 bg-gray-100 rounded-lg">
                        {this.state.error?.toString()}
                        <br />
                        {this.state.errorInfo?.componentStack}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="bg-gray-50 py-6 border-t border-gray-100">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-3 order-2 sm:order-1">
                  <p className="text-sm text-gray-500">
                    © {new Date().getFullYear()} QNotes AI. All rights reserved.
                  </p>
                </div>
                <div className="flex items-center gap-6 order-1 sm:order-2">
                  <div className="text-right">
                    <h2 className="text-lg font-semibold text-gray-800">QNotes AI</h2>
                    <p className="text-sm text-gray-500">
                      Your AI-powered note-taking assistant
                    </p>
                  </div>
                  <img 
                    src="/logo.png" 
                    alt="QNotes AI Logo" 
                    className="w-10 h-10"
                  />
                </div>
              </div>
            </div>
          </footer>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 
